import { useState, useEffect } from "react";
import { getEmployeeNameById } from "../utils/employeeNames";

/**
 * Performance Report - Employee performance analysis
 * Admin-only report showing time tracked, jobs completed, efficiency metrics
 */
export default function PerformanceReport({ onBackToApp }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  // Filters
  const [dateRange, setDateRange] = useState("month");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [workCenterFilter, setWorkCenterFilter] = useState("");

  // Tab view
  const [activeTab, setActiveTab] = useState("overview");

  const getDateParams = () => {
    const now = new Date();
    let startDate, endDate;

    switch (dateRange) {
      case "today":
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
        break;
      case "week":
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString();
        break;
      case "month":
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString();
        break;
      case "quarter":
        const quarterAgo = new Date(now);
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        startDate = quarterAgo.toISOString();
        break;
      case "custom":
        startDate = customStartDate ? new Date(customStartDate).toISOString() : undefined;
        endDate = customEndDate ? new Date(customEndDate + "T23:59:59").toISOString() : undefined;
        break;
      default:
        break;
    }

    return { startDate, endDate };
  };

  useEffect(() => {
    loadData();
  }, [dateRange, customStartDate, customEndDate, selectedEmployee, workCenterFilter]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = sessionStorage.getItem("msal.backendToken");
      const { startDate, endDate } = getDateParams();

      let url = "/api/reports/performance";
      const params = new URLSearchParams();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (selectedEmployee) params.set("employeeId", selectedEmployee);
      if (workCenterFilter) params.set("workCenter", workCenterFilter);
      if (params.toString()) url += "?" + params.toString();

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 403) {
        setError("Access denied. This report is admin-only.");
        return;
      }

      if (!res.ok) throw new Error("Failed to load performance data");

      const result = await res.json();
      setData(result);
    } catch (err) {
      console.error("Error loading performance:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Format duration
  const formatHours = (hours) => {
    if (!hours) return "0h";
    return `${Math.round(hours * 10) / 10}h`;
  };

  return (
    <div className="flex-1 overflow-auto bg-slate-100 text-gray-900 flex flex-col">

      {/* CONTROLS BAR */}
      <div className="bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-4 flex-wrap">
          {/* Date range */}
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-500">Range:</label>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last 90 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom...</option>
            </select>
          </div>

          {/* Custom date inputs */}
          {dateRange === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
                className="bg-slate-100 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
                className="bg-slate-100 border border-gray-300 rounded-md px-2 py-1.5 text-sm"
              />
            </div>
          )}

          {/* Employee filter */}
          {data?.employees && (
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-500">Employee:</label>
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="bg-slate-100 border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                <option value="">All Employees</option>
                {data.employees.map(emp => (
                  <option key={emp.employeeId} value={emp.employeeId}>
                    {getEmployeeNameById(emp.employeeId)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* SUMMARY STATS */}
      {data?.teamAverage && data?.summary && (
        <div className="bg-slate-100 border-b border-gray-200 py-4 px-4">
          <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <div className="text-3xl font-bold text-orange-400">
                {data.summary.employeeCount}
              </div>
              <div className="text-xs text-gray-500">Active Employees</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-400">
                {data.summary.workDays}
              </div>
              <div className="text-xs text-gray-500">Work Days</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-400">
                {data.teamAverage.hoursPerDay}h
              </div>
              <div className="text-xs text-gray-500">Avg Hours/Day/Person</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">
                {data.teamAverage.jobsPerDay}
              </div>
              <div className="text-xs text-gray-500">Avg Jobs/Day/Person</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-cyan-600">
                {data.teamAverage.ticketsPerDay}
              </div>
              <div className="text-xs text-gray-500">Avg Tickets/Day/Person</div>
            </div>
          </div>
        </div>
      )}

      {/* TABS */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="max-w-7xl mx-auto flex items-center gap-1">
          {[
            { id: "overview", label: "By Employee" },
            { id: "trend", label: "Daily Trend" },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? "border-orange-500 text-orange-400"
                  : "border-transparent text-gray-500 hover:text-gray-900"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-auto p-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-gray-500">Loading performance data...</div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-red-400">{error}</div>
            </div>
          ) : (
            <>
              {/* BY EMPLOYEE TAB */}
              {activeTab === "overview" && data?.employees && (
                <div className="bg-slate-100 rounded-lg overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-white">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Employee</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Total Hours</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Hours/Day</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Jobs</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Jobs/Day</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tickets</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Avg/Ticket</th>
                        <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Pieces</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {data.employees.map((emp, idx) => {
                        // Compare to team average
                        const hoursVsAvg = data.teamAverage.hoursPerDay > 0
                          ? ((emp.avgHoursPerDay - data.teamAverage.hoursPerDay) / data.teamAverage.hoursPerDay) * 100
                          : 0;
                        return (
                          <tr key={emp.employeeId || idx} className="hover:bg-slate-100/30">
                            <td className="px-4 py-3">
                              <span className="font-medium">{getEmployeeNameById(emp.employeeId)}</span>
                            </td>
                            <td className="px-4 py-3 text-right text-blue-400 font-mono text-lg">
                              {formatHours(emp.totalHours)}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="font-mono">{emp.avgHoursPerDay}</span>
                              {hoursVsAvg !== 0 && (
                                <span className={`ml-1 text-xs ${hoursVsAvg > 0 ? "text-green-400" : "text-red-400"}`}>
                                  ({hoursVsAvg > 0 ? "+" : ""}{Math.round(hoursVsAvg)}%)
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right text-purple-600 font-mono">
                              {emp.jobsCompleted?.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 font-mono">
                              {emp.avgJobsPerDay}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-700 font-mono">
                              {emp.ticketCount?.toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right text-cyan-600">
                              {emp.avgTicketMinutes}m
                            </td>
                            <td className="px-4 py-3 text-right text-gray-500 font-mono">
                              {emp.totalPieces?.toLocaleString() || 0}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {/* Totals row */}
                    <tfoot className="bg-white border-t-2 border-gray-300">
                      <tr>
                        <td className="px-4 py-3 font-semibold">Team Total</td>
                        <td className="px-4 py-3 text-right text-blue-400 font-mono font-semibold">
                          {formatHours(data.employees.reduce((sum, e) => sum + e.totalHours, 0))}
                        </td>
                        <td className="px-4 py-3 text-right font-mono">
                          {data.teamAverage.hoursPerDay} avg
                        </td>
                        <td className="px-4 py-3 text-right text-purple-600 font-mono font-semibold">
                          {data.employees.reduce((sum, e) => sum + e.jobsCompleted, 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 font-mono">
                          {data.teamAverage.jobsPerDay} avg
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 font-mono font-semibold">
                          {data.employees.reduce((sum, e) => sum + e.ticketCount, 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right text-cyan-600">—</td>
                        <td className="px-4 py-3 text-right text-gray-500 font-mono font-semibold">
                          {data.employees.reduce((sum, e) => sum + (e.totalPieces || 0), 0).toLocaleString()}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {/* DAILY TREND TAB */}
              {activeTab === "trend" && data?.dailyTrend && (
                <div className="space-y-4">
                  {/* Simple bar visualization */}
                  <div className="bg-slate-100 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-gray-500 mb-4">Team Activity by Day</h3>
                    <div className="space-y-2">
                      {data.dailyTrend.slice(0, 14).reverse().map((day, idx) => {
                        const maxHours = Math.max(...data.dailyTrend.map(d => (d.totalSeconds || 0) / 3600));
                        const hours = (day.totalSeconds || 0) / 3600;
                        const width = maxHours > 0 ? (hours / maxHours) * 100 : 0;
                        return (
                          <div key={day.date || idx} className="flex items-center gap-3">
                            <div className="w-24 text-xs text-gray-500 shrink-0">
                              {new Date(day.date).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}
                            </div>
                            <div className="flex-1 bg-gray-200 rounded h-6 overflow-hidden">
                              <div
                                className="h-full bg-gradient-to-r from-orange-600 to-pink-500 flex items-center justify-end pr-2"
                                style={{ width: `${width}%` }}
                              >
                                <span className="text-xs font-medium">{Math.round(hours)}h</span>
                              </div>
                            </div>
                            <div className="w-20 text-right text-xs text-gray-500">
                              {day.activeEmployees} people
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Trend table */}
                  <div className="bg-slate-100 rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-white">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-500">Date</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Active Employees</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Total Hours</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Tickets</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Jobs</th>
                          <th className="text-right px-4 py-3 text-sm font-medium text-gray-500">Avg Hours/Person</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {data.dailyTrend.map((day, idx) => {
                          const hours = (day.totalSeconds || 0) / 3600;
                          const avgHours = day.activeEmployees > 0 ? hours / day.activeEmployees : 0;
                          return (
                            <tr key={day.date || idx} className="hover:bg-slate-100/30">
                              <td className="px-4 py-3 font-medium">
                                {new Date(day.date).toLocaleDateString("en-US", {
                                  weekday: "short",
                                  month: "short",
                                  day: "numeric"
                                })}
                              </td>
                              <td className="px-4 py-3 text-right text-orange-400 font-mono">
                                {day.activeEmployees}
                              </td>
                              <td className="px-4 py-3 text-right text-blue-400 font-mono">
                                {Math.round(hours * 10) / 10}h
                              </td>
                              <td className="px-4 py-3 text-right text-gray-700 font-mono">
                                {day.ticketCount?.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-right text-purple-600 font-mono">
                                {day.jobsCompleted?.toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-right text-cyan-600 font-mono">
                                {Math.round(avgHours * 10) / 10}h
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}
